<svelte:options
	customElement={{
		tag: "contact-modal",
		shadow: "none",
	}}
/>

<script module>
	export const OpenContactModalEvent = "open-contact-modal";
</script>
<script lang="ts">
	import { onMount } from "svelte";
	import { fade, scale } from "svelte/transition";
	import { quintOut } from "svelte/easing";
	import ky from "ky";
	import { enforce, test, omitWhen } from "vest";
	import "vest/email";
	import { control, ValidatingForm } from "@wagich/form-validation";

	import { initFluentContext, Localized, localize } from "@/localization";
	import SvgIcon from "@/components/svg-icon.svelte";
	import { trackEvent } from "@/analytics";
	import type { ContactRequest } from "@/models";
	import { FormState } from "@/utils";

	const initialFormData: ContactRequest = {
		fullname: "",
		email: "",
		phone: "",
		address: "",
		postalCode: "",
		city: "",
		message: "",
		subscribeToNewsletter: false,
		newsletterSegment: ""
	};

	let isOpen: boolean = $state(false);
	let formState: FormState = $state(FormState.Ready);
	let token: string = "";

	initFluentContext();

	const form = new ValidatingForm<ContactRequest>(initialFormData, (data) => {
		test("fullname", localize("contactform-fullname.error-required"), () => {
			enforce(data.fullname).isNotBlank();
		});
		test("email", () => {
			enforce(data.email)
				.message(localize("contactform-email.error-required")).isNotBlank()
				.message(localize("contactform-email.error-format")).isEmail();
		});
		test("phone", localize("contactform-phone.error-required"), () => {
			enforce(data.phone).isNotBlank();
		});
		test("message", localize("contactform-message.error-required"), () => {
			enforce(data.message).isNotBlank();
		});

		omitWhen(!data.subscribeToNewsletter, () => {
			test("newsletterSegment", localize("contactform-segment.error-required"), () => {
				enforce(data.newsletterSegment).isNotBlank();
			});
		});
	});

	onMount(() => {
		let tokenElement = document.getElementById("RequestVerificationToken");
		if (tokenElement == null || tokenElement.textContent == null) {
			throw new Error("Could not load XSRF token.");
		}
		token = JSON.parse(tokenElement.textContent);

		document.addEventListener(OpenContactModalEvent, open);
	});

	async function submitAsync() {
		if (formState !== FormState.Ready) {
			return;
		}
		if (!form.validate()) {
			return;
		}

		try {
			formState = FormState.Loading;
			await ky.post(`/api/contact`, {
				headers: { RequestVerificationToken: token },
				json: {
					sourcePath: window.location.pathname,
					sourceTitle: document.title,
					language: document.documentElement.lang,
					...form.getData()
				},
			});
			formState = FormState.Success;
		} catch (e) {
			formState = FormState.Error;
		}
	}

	function open() {
		isOpen = true;
	}

	function close() {
		isOpen = false;
		form.reset();
	}
</script>

{#if isOpen}
	<div class="modal is-active">
		<!-- svelte-ignore a11y_click_events_have_key_events, a11y_no_static_element_interactions -->
		<div class="modal-background" onclick={close} transition:fade={{ duration: 200 }}></div>
		<div class="modal-card" transition:scale={{ duration: 250, start: 0.9, easing: quintOut }}>
			<header class="modal-card-head">
				<div class="modal-card-title is-family-secondary has-text-primary">
					<Localized id="contactform-title" />
				</div>
				<button class="button is-ghost has-text-white modal-close-button" aria-label="close" onclick={close}>
					<SvgIcon size="is-medium" icon="close"/>
				</button>
			</header>
			<section class="modal-card-body">
				<div class="field">
					<label for="fullname" class="label is-small">
						<Localized id="contactform-fullname" />
					</label>
					<input id="fullname" class="input" type="text" {@attach control(form.fields.fullname)} autocomplete="name" />
					{#each form.fields.fullname.errors as error}
						<p class="help is-danger">{error}</p>
					{/each}
				</div>
				<div class="columns">
					<div class="column is-6">
						<div class="field">
							<label for="email" class="label is-small">
								<Localized id="contactform-email" />
							</label>
							<input id="email" class="input" type="email" {@attach control(form.fields.email)} autocomplete="email" />
							{#each form.fields.email.errors as error}
								<p class="help is-danger">{error}</p>
							{/each}
						</div>
					</div>
					<div class="column is-6">
						<div class="field">
							<label for="phone" class="label is-small">
								<Localized id="contactform-phone" />
							</label>
							<input id="phone" class="input" type="text" {@attach control(form.fields.phone)} autocomplete="tel" />
							{#each form.fields.phone.errors as error}
								<p class="help is-danger">{error}</p>
							{/each}
						</div>
					</div>
				</div>

				<div class="field">
					<label for="address" class="label is-small">
						<Localized id="contactform-address" />
					</label>
					<input id="address" class="input" type="text" {@attach control(form.fields.address)} autocomplete="address-line1" />
					{#each form.fields.address.errors as error}
						<p class="help is-danger">{error}</p>
					{/each}
				</div>
				<div class="columns">
					<div class="column is-4">
						<div class="field">
							<label for="postalCode" class="label is-small">
								<Localized id="contactform-postalcode" />
							</label>
							<input id="postalCode" class="input" type="text" {@attach control(form.fields.postalCode)} autocomplete="postal-code" />
							{#each form.fields.postalCode.errors as error}
								<p class="help is-danger">{error}</p>
							{/each}
						</div>
					</div>
					<div class="column is-8">
						<div class="field">
							<label for="city" class="label is-small">
								<Localized id="contactform-city" />
							</label>
							<input id="city" class="input" type="text" {@attach control(form.fields.city)} autocomplete="address-level2" />
							{#each form.fields.city.errors as error}
								<p class="help is-danger">{error}</p>
							{/each}
						</div>
					</div>
				</div>

				<div class="field">
					<label for="message" class="label is-small">
						<Localized id="contactform-message" />
					</label>
					<textarea id="message" class="textarea" {@attach control(form.fields.message)}></textarea>
					{#each form.fields.message.errors as error}
						<p class="help is-danger">{error}</p>
					{/each}
				</div>

				<div class="field">
					<input type="checkbox" class="is-checkradio is-light" {@attach control(form.fields.subscribeToNewsletter)} id="subscribeToNewsletter" />
					<label class="is-flex" for="subscribeToNewsletter">
						<Localized id="contactform-newsletter" />
					</label>
				</div>
				{#if form.fields.subscribeToNewsletter.value}
					<div class="field mb-4">
						<label class="label is-small">
							<Localized id="contactform-segment" />
						</label>
						<div class="control">
							<div class="mb-2">
								<input type="radio" class="is-checkradio is-light" value="architect" id="newsletterSegmentArchitect3" {@attach control(form.fields.newsletterSegment)} />
								<label class="radio" for="newsletterSegmentArchitect3">
									<Localized id="contactform-segment-architect" />
								</label>
							</div>
							<div>
								<input type="radio" class="is-checkradio is-light" value="consumer" id="newsletterSegmentConsumer3" {@attach control(form.fields.newsletterSegment)} />
								<label class="radio" for="newsletterSegmentConsumer3">
									<Localized id="contactform-segment-consumer" />
								</label>
							</div>
						</div>
						{#each form.fields.newsletterSegment.errors as error}
							<p class="help is-danger">{error}</p>
						{/each}
					</div>
				{/if}
			</section>
			{#if formState === FormState.Ready || formState === FormState.Loading}
				<footer class="modal-card-foot">
					<button type="button" class="button is-primary" class:is-loading={formState === FormState.Loading} onclick={submitAsync}>
						<Localized id="contactform-submit" />
					</button>
				</footer>
			{:else if formState === FormState.Success}
				<footer class="modal-card-foot notification is-success mt-0 pt-2">
					<Localized id="contactform-success" />
				</footer>
			{:else if formState === FormState.Error}
				<footer class="modal-card-foot notification is-danger mt-0 pt-2">
					<Localized id="contactform-error" />
				</footer>
			{/if}
		</div>
	</div>
{/if}
